import { useState } from 'react';

import { LogIn, LogOut, Crown, Save } from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { patch, post } from '../../api/client';
import { useEvents } from '../../context/EventContext';
import { useToast } from '../../context/ToastContext';
import EventPicker from '../../components/EventPicker';
import {
  Button,
  Modal,
  Field,
  Input,
  Select,
  Table,
  Badge,
  StatusBadge,
  Avatar,
  SectionTitle,
  Pagination,
  SearchInput,
  ConfirmModal,
  Tabs,
  label,
} from '../../components/UI';
import { fmtDateTime } from '../../utils/format';

export default function HotelRooms() {
  const { eventId } = useEvents();
  const toast = useToast();

  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const { data: rows, meta, loading, reload } = useFetch(
    '/rooms',
    { page, limit: 25, ...(eventId ? { event: eventId } : {}), ...(status ? { status } : {}) },
    { withMeta: true },
  );

  const visible = search
    ? (rows || []).filter((r) =>
        r.roomNumber?.includes(search) ||
        r.occupants?.some((o) => o.name?.toLowerCase().includes(search.toLowerCase())))
    : rows;

  const act = async () => {
    const { room, action } = confirm;
    try {
      const res = await post(`/rooms/${room._id}/${action}`, {});
      toast.success(res.message);
      setConfirm(null);
      reload();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <>
      <SectionTitle
        title="Rooms"
        subtitle="Assign room numbers, then check guests in and out"
        action={<EventPicker />}
      />

      <Tabs
        active={status}
        onChange={(k) => { setStatus(k); setPage(1); }}
        tabs={[
          { key: '', label: 'All rooms' },
          { key: 'reserved', label: 'Awaiting arrival' },
          { key: 'occupied', label: 'Occupied' },
          { key: 'checked_out', label: 'Checked out' },
        ]}
      />

      <SearchInput value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by room number or guest name…" style={{ maxWidth: 380, marginBottom: 16 }} />

      <Table
        loading={loading}
        rows={visible}
        onRowClick={setEditing}
        empty="No rooms allocated to your property for this event yet"
        columns={[
          { key: 'roomNumber', title: 'Room', render: (r) => (
            <span style={{
              fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700,
              color: r.roomNumber ? 'var(--gold-300)' : 'var(--text-lo)',
            }}>
              {r.roomNumber || 'Unassigned'}
            </span>
          )},
          { key: 'category', title: 'Category', render: (r) => (
            <div>
              <div style={{ fontSize: 12.8 }}>{r.category || '—'}</div>
              <div style={{ fontSize: 11, color: 'var(--text-lo)' }}>{r.bedType ? label(r.bedType) : ''}</div>
            </div>
          )},
          { key: 'occupants', title: 'Guests', render: (r) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {r.occupants?.length ? r.occupants.map((o) => (
                <div key={o._id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Avatar name={o.name} size={22} vip={o.isVip} />
                  <span style={{ fontSize: 12.5 }}>{o.name}</span>
                  {o.isVip && <Crown size={11} color="var(--gold-400)" />}
                </div>
              )) : <span style={{ color: 'var(--text-lo)' }}>—</span>}
            </div>
          )},
          { key: 'groupName', title: 'Group', render: (r) => <Badge tone="neutral">{r.groupName || 'Unassigned'}</Badge> },
          { key: 'status', title: 'Status', render: (r) => <StatusBadge status={r.status} /> },
          { key: 'actions', title: '', align: 'right', render: (r) => (
            <div style={{ display: 'flex', gap: 7, justifyContent: 'flex-end' }}>
              {r.status !== 'occupied' && r.status !== 'checked_out' && (
                <Button size="sm" variant="outline" icon={LogIn} disabled={!r.roomNumber}
                  onClick={(e) => { e.stopPropagation(); setConfirm({ room: r, action: 'check-in' }); }}>
                  Check in
                </Button>
              )}
              {r.status === 'occupied' && (
                <Button size="sm" variant="ghost" icon={LogOut}
                  onClick={(e) => { e.stopPropagation(); setConfirm({ room: r, action: 'check-out' }); }}>
                  Check out
                </Button>
              )}
            </div>
          )},
        ]}
      />
      <Pagination meta={meta} onChange={setPage} />

      <RoomEditor room={editing} onClose={() => setEditing(null)} onDone={reload} />

      <ConfirmModal
        open={Boolean(confirm)} onClose={() => setConfirm(null)} onConfirm={act}
        title={confirm?.action === 'check-in' ? `Check in room ${confirm?.room?.roomNumber}?` : `Check out room ${confirm?.room?.roomNumber}?`}
        confirmLabel={confirm?.action === 'check-in' ? 'Check in' : 'Check out'}
        message={confirm?.action === 'check-in'
          ? 'Guests will be notified of their room number and who they are sharing with.'
          : 'The room will be marked available for housekeeping.'}
      />
    </>
  );
}

function RoomEditor({ room, onClose, onDone }) {
  const toast = useToast();
  const [form, setForm] = useState({ roomNumber: '', floor: '', category: '', bedType: 'twin', keyCardsIssued: 0, notes: '' });
  const [busy, setBusy] = useState(false);

  /* Load the room into the form whenever a new one is opened. */
  const [loadedId, setLoadedId] = useState(null);
  if (room && room._id !== loadedId) {
    setLoadedId(room._id);
    setForm({
      roomNumber: room.roomNumber || '',
      floor: room.floor || '',
      category: room.category || '',
      bedType: room.bedType || 'twin',
      keyCardsIssued: room.keyCardsIssued || 0,
      notes: room.notes || '',
    });
  }

  const save = async () => {
    setBusy(true);
    try {
      await patch(`/rooms/${room._id}`, {
        ...form,
        keyCardsIssued: Number(form.keyCardsIssued) || 0,
      });
      toast.success(form.roomNumber ? `Room ${form.roomNumber} updated — guests notified` : 'Room updated');
      onDone();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={Boolean(room)} onClose={onClose}
      title="Room details"
      subtitle={room?.occupants?.map((o) => o.name).join(' · ') || 'No guests assigned'}
      footer={
        <>
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button icon={Save} onClick={save} loading={busy}>Save room</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
        <div style={{
          padding: 13, background: 'rgba(239,212,138,0.06)',
          border: '1px solid rgba(239,212,138,0.18)', borderRadius: 'var(--r)',
        }}>
          <p style={{ fontSize: 12.3, color: 'var(--text-mid)', lineHeight: 1.6 }}>
            Assigning a room number sends each guest a WhatsApp and push
            notification with their room and sharing partner.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
          <Field label="Room number" required>
            <Input value={form.roomNumber} onChange={(e) => setForm({ ...form, roomNumber: e.target.value })}
              placeholder="504" autoFocus />
          </Field>
          <Field label="Floor">
            <Input value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} placeholder="5" />
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
          <Field label="Category">
            <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Deluxe" />
          </Field>
          <Field label="Bed type">
            <Select value={form.bedType} onChange={(e) => setForm({ ...form, bedType: e.target.value })}>
              {['single', 'twin', 'double', 'suite'].map((b) => <option key={b} value={b}>{label(b)}</option>)}
            </Select>
          </Field>
        </div>

        <Field label="Key cards issued">
          <Input type="number" min={0} value={form.keyCardsIssued}
            onChange={(e) => setForm({ ...form, keyCardsIssued: e.target.value })} />
        </Field>

        <Field label="Front desk notes" hint="Visible only to your property">
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="e.g. Requested a high floor away from the lift" />
        </Field>

        {room?.checkInAt && (
          <p style={{ fontSize: 12, color: 'var(--text-lo)' }}>
            Checked in {fmtDateTime(room.checkInAt)}
            {room.checkOutAt ? ` · Checked out ${fmtDateTime(room.checkOutAt)}` : ''}
          </p>
        )}
      </div>
    </Modal>
  );
}
